// components/Content/UserInvestments.js
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  useGetUserInvestmentsQuery, 
  useSendPayoutMutation,
  useUpdateInvestmentStatusMutation 
} from '../../features/user/userApi';
import PayoutModal from '../../components/Content/PayoutModal';
import { format } from 'date-fns';

const UserInvestments = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromPage = queryParams.get('fromPage') || 1;
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const { 
    data: investments, 
    isLoading: investmentsLoading, 
    isError: investmentsError,
    refetch: refetchInvestments
  } = useGetUserInvestmentsQuery({ userId, page, limit });
  
  const [sendPayout, { isLoading: isSending }] = useSendPayoutMutation();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateInvestmentStatusMutation();

  const handlePayoutClick = (investment) => {
    setSelectedInvestment(investment);
    setShowModal(true);
  };

  const handleConfirmPayout = async () => {
    try {
      await sendPayout({
        userId: selectedInvestment.userId,
        investmentId: selectedInvestment.id
      }).unwrap();
      setShowModal(false);
      refetchInvestments();
    } catch (error) {
      console.error('Failed to send payout:', error);
    }
  };

  const handleUpdateStatus = async (investmentId) => {
    try {
      await updateStatus({ investmentId }).unwrap();
      refetchInvestments();
    } catch (error) {
      console.error('Failed to update investment status:', error);
    }
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < investments?.totalPages) setPage(page + 1);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleBackToUsers = () => {
    navigate(`/user-management?page=${fromPage}`);
  };

  if (investmentsLoading) return <div className="text-center py-8">Loading investments...</div>;
  if (investmentsError) return <div className="text-center py-8 text-red-500">Error loading investments</div>;

  return (
    <div className="space-y-8 p-4">
      <button 
        onClick={handleBackToUsers}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
        Back to Users
      </button>

      <h2 className="text-2xl font-bold">User Investments</h2>

      {/* Pagination Controls - Top */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm">Show:</span>
          <select 
            value={limit}
            onChange={handleLimitChange}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm">entries</span>
        </div>
        
        <div className="text-sm text-gray-600">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, investments?.total || 0)} of {investments?.total || 0} investments
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Complete</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {investments?.data?.map((investment) => (
                <tr key={investment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{investment.InvestmentPlan.planName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900">₹ {investment.investedAmount}</div>
                    <div className="text-gray-500 text-sm">Expected: ₹ {investment.expectedReturn}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900">
                      {format(new Date(investment.startDate), 'MMM d, yyyy')} - {format(new Date(investment.endDate), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      investment.status === 'active' ? 'bg-green-100 text-green-800' : 
                      investment.status === 'completed' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {investment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {investment.status === 'active' && (
                      <button
                        onClick={() => handlePayoutClick(investment)}
                        disabled={isSending}
                        className={`px-3 py-1 rounded text-sm ${
                          isSending ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {isSending ? 'Processing...' : 'Send Payout'}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {investment.status !== 'completed' && (
                      <button
                        onClick={() => handleUpdateStatus(investment.id)}
                        disabled={isUpdating}
                        className={`px-3 py-1 rounded text-sm ${
                          isUpdating ? 'bg-gray-300' : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {isUpdating ? 'Updating...' : 'Mark Complete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls - Bottom */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-sm text-gray-600">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, investments?.total || 0)} of {investments?.total || 0} investments
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrevious}
            disabled={page === 1}
            className={`px-3 py-1 text-sm rounded-md ${
              page === 1 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          
          <span className="px-3 py-1 text-sm text-gray-700">
            Page {page} of {investments?.totalPages || 1}
          </span>
          
          <button
            onClick={handleNext}
            disabled={page === investments?.totalPages}
            className={`px-3 py-1 text-sm rounded-md ${
              page === investments?.totalPages 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {showModal && (
        <PayoutModal
          investment={selectedInvestment}
          onConfirm={handleConfirmPayout}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default UserInvestments;